/**
 * Sipariş ekranındaki "Bilgilendirme Mailleri" alanı için adres listesi düzenleme.
 *
 * Kullanıcı adresleri virgül, noktalı virgül, boşluk ya da satır sonuyla ayırabilir; hatta
 * araya hiçbir şey koymadan yapıştırabilir ("a@x.comb@y.net"). Hepsi tek biçime çevrilir:
 * "a@x.com, b@y.net". Sunucu da aynı listeyi doğrular; burada yalnız kullanıcıya erkenden
 * gösterilir ve Kaydet engellenir.
 */

/**
 * Bitişik adresleri ayırırken tanınan alan adı uzantıları. İki düzeyli uzantılar
 * (".com.tr") tek düzeylilerden (".com") önce denenir: en uzun eşleşme tercih edilir.
 */
const UZANTILAR: string[] = [
    // İkinci düzey (ülke altı) uzantılar
    'com.tr', 'net.tr', 'org.tr', 'gov.tr', 'edu.tr', 'gen.tr', 'biz.tr', 'info.tr', 'av.tr', 'bel.tr',
    'k12.tr', 'pol.tr', 'tsk.tr', 'web.tr', 'tv.tr', 'dr.tr', 'name.tr', 'tel.tr', 'bbs.tr', 'mil.tr',
    'co.uk', 'org.uk', 'ac.uk', 'gov.uk', 'me.uk', 'ltd.uk', 'plc.uk',
    'com.az', 'net.az', 'org.az', 'gov.az', 'edu.az',
    'com.de', 'com.ru', 'co.jp', 'co.kr', 'co.za', 'co.in', 'com.au', 'com.br', 'com.cn', 'com.cy', 'com.gr', 'com.ua',
    // Genel uzantılar
    'com', 'net', 'org', 'info', 'biz', 'io', 'co', 'edu', 'gov', 'mil', 'int', 'name', 'pro', 'mobi', 'tel',
    'app', 'dev', 'online', 'site', 'store', 'shop', 'xyz', 'tech', 'cloud', 'email', 'website', 'group',
    'global', 'agency', 'digital', 'company', 'solutions', 'services', 'systems', 'network', 'media', 'world',
    'live', 'life', 'link', 'space', 'today', 'center', 'tv', 'ai', 'me', 'cc', 'ws', 'asia', 'istanbul',
    // Ülke uzantıları
    'eu', 'tr', 'de', 'uk', 'us', 'az', 'ru', 'nl', 'fr', 'it', 'es', 'pt', 'be', 'at', 'ch', 'se', 'no', 'dk',
    'fi', 'pl', 'cz', 'sk', 'hu', 'ro', 'bg', 'gr', 'cy', 'ua', 'by', 'kz', 'uz', 'ge', 'am', 'ir', 'iq', 'sa',
    'ae', 'qa', 'kw', 'eg', 'il', 'cn', 'jp', 'kr', 'in', 'au', 'nz', 'ca', 'mx', 'br', 'ar', 'za', 'ie', 'lt',
    'lv', 'ee', 'rs', 'hr', 'si', 'ba', 'mk', 'al', 'md', 'tm', 'kg', 'tj', 'ly', 'tn', 'ma', 'dz', 'pk', 'af',
].sort((a, b) => b.length - a.length);

/** Adres biçimi: yerel kısım @ en az bir noktalı alan adı, son etiket en az iki harf. */
const MAIL_DESENI = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

/**
 * İki "@" arasındaki metni (ilk adresin alan adı + ikinci adresin yerel kısmı) ayırır.
 * Aday: bir noktadan başlayan bilinen uzantı, ardından nokta OLMAYAN bir karakter gelir
 * (".com" ardından "." gelirse ".com.tr" gibi devam ediyordur, ayrılmaz).
 * Adaylardan en uzun uzantılı olan, eşitse en sondaki seçilir. Böylece "x.comb" → "x.com" + "b",
 * "x.com.trb" → "x.com.tr" + "b", "mail.demir.com.trb" → "mail.demir.com.tr" + "b" (".de" seçilmez).
 */
function ortaParcayiAyir(parca: string): { alanAdi: string, yerel: string } | undefined {
    // Yalnız ASCII harfler küçültülür: "İ".toLowerCase() iki karakter olup konumları kaydırır.
    const kucuk = parca.replace(/[A-Z]/g, c => c.toLowerCase());
    let enIyi: { bitis: number, uzunluk: number } | undefined;

    for (let i = 1; i < kucuk.length; i++) {
        if (kucuk[i] !== '.' || kucuk[i - 1] === '.')
            continue;

        for (const uzanti of UZANTILAR) {
            const bitis = i + 1 + uzanti.length;

            // Ardından en az bir karakter (ikinci adresin yerel kısmı) olmalı ve bu nokta olmamalı.
            if (bitis >= kucuk.length || kucuk[bitis] === '.')
                continue;

            if (kucuk.startsWith(uzanti, i + 1)) {
                if (!enIyi || uzanti.length > enIyi.uzunluk || (uzanti.length === enIyi.uzunluk && bitis > enIyi.bitis))
                    enIyi = { bitis, uzunluk: uzanti.length };

                // Bu noktada en uzun eşleşme bulundu; daha kısası denenmez.
                break;
            }
        }
    }

    if (!enIyi)
        return undefined;

    return { alanAdi: parca.substring(0, enIyi.bitis), yerel: parca.substring(enIyi.bitis) };
}

/** Bir parçada birden çok "@" varsa bitişik yazılmış adresleri ayırır. */
function bitisikAdresleriAyir(parca: string): string[] {
    const bolumler = parca.split('@');

    if (bolumler.length <= 2)
        return [parca];

    const adresler: string[] = [];
    let mevcut = bolumler[0];

    for (let i = 1; i < bolumler.length - 1; i++) {
        const ayrim = ortaParcayiAyir(bolumler[i]);

        if (ayrim) {
            adresler.push(mevcut + '@' + ayrim.alanAdi);
            mevcut = ayrim.yerel;
        }
        else
            // Ayrılamadı (bilinmeyen uzantı): olduğu gibi kalır, doğrulamada geçersiz görünür.
            mevcut = mevcut + '@' + bolumler[i];
    }

    adresler.push(mevcut + '@' + bolumler[bolumler.length - 1]);

    return adresler;
}

/**
 * Metni "a@x.com, b@y.net" biçimine getirir.
 * - Ayraçlar: virgül, noktalı virgül, boşluk (sekme dahil), satır sonu. Boş parçalar atılır.
 * - Outlook'tan kopyalanan "Ad Soyad <adres>" biçiminde yalnız adres alınır.
 * - Bitişik adresler bilinen uzantıya göre ayrılır.
 * Büyük/küçük harf ve adreslerin sırası korunur; tekrar eden adres silinmez.
 */
export function mailListesiDuzenle(metin: string | undefined | null): string {
    if (!metin)
        return '';

    const adSoyadsiz = metin.replace(/[^,;<>\r\n]*<([^<>\s]+@[^<>\s]+)>/g, ' $1 ');

    return adSoyadsiz
        .split(/[,;\s]+/)
        .map(x => x.trim())
        .filter(x => x.length > 0)
        .flatMap(bitisikAdresleriAyir)
        .join(', ');
}

/** Düzenlenmiş listedeki geçersiz adresler (her biri bir kez, yazıldığı sırayla). */
export function gecersizMailler(metin: string | undefined | null): string[] {
    const duzenli = mailListesiDuzenle(metin);

    if (!duzenli)
        return [];

    const gecersizler = duzenli.split(', ').filter(x => !MAIL_DESENI.test(x));

    return gecersizler.filter((x, i) => gecersizler.indexOf(x) === i);
}
